import { useEffect, useRef } from "react";
import { YStack } from "tamagui";

import { PolicyStatus } from "@/api/generated/models";
import { LoadingSpinner, Paragraph } from "@/components";
import { usePatientResponsibility } from "@/conversation/sidebar/Payment/usePatientResponsibility";

export type InsuranceVerifyReason = "pending" | "unverified";

export default function InsuranceVerificationStep({
  hasAffiliation,
  onVerified,
  onFailed,
}: {
  hasAffiliation: boolean;
  onVerified: () => void;
  onFailed: (reason: InsuranceVerifyReason) => void;
}) {
  const { isLoading, isInsuranceEnabled, policyStatus } =
    usePatientResponsibility({ enabled: !hasAffiliation });

  const resolvedRef = useRef(false);
  useEffect(() => {
    if (resolvedRef.current) return;
    if (hasAffiliation) {
      resolvedRef.current = true;
      onVerified();
      return;
    }
    if (isLoading) return;
    resolvedRef.current = true;
    if (isInsuranceEnabled) {
      onVerified();
    } else {
      onFailed(
        policyStatus === PolicyStatus.PENDING ? "pending" : "unverified",
      );
    }
  }, [
    hasAffiliation,
    isLoading,
    isInsuranceEnabled,
    policyStatus,
    onVerified,
    onFailed,
  ]);

  return (
    <YStack
      alignItems="center"
      justifyContent="center"
      paddingVertical="$400"
      gap="$200"
    >
      <LoadingSpinner size="$900" />
      <Paragraph color="$textSecondary">Verifying your insurance…</Paragraph>
    </YStack>
  );
}
